import React, { useState, useEffect, Suspense, lazy } from "react";
import CrossImpactAssessment from "./CrossImpactAssessment";
import { AudioPlayer } from "./components/AudioPlayer";
import PulledQuote from "./components/PulledQuote";
import EngravingImage from "./components/EngravingImage";
import OperationalMap from "./components/OperationalMap";
import PaywallBreak from "./components/PaywallBreak";
import { PEWidget } from "./components/PEWidget";
import "./components/editorial.css";

// Code-split the two heavy visuals (mapbox-gl, d3-sankey) out of the main
// bundle — they load on demand when a briefing renders, cutting initial JS.
const GeographicContextMap = lazy(() => import("./GeographicContextMap"));

const VisualFallback = ({ label }) => (
  <div style={{ padding: 24, textAlign: "center", color: "var(--color-text-light)", fontStyle: "italic", fontSize: 13 }}>
    {label}
  </div>
);

const API_URL = process.env.REACT_APP_API_URL || "https://api.bearing-intelligence.com";

// Key Development tile lookups (domain-colored accents, filled severity badges, flags).
const DOMAIN_COLORS = {
  MILITARY: "#7A1E1E", POLITICAL: "#1A3655", ECONOMIC: "#1A4D2E", DIPLOMATIC: "#6B4A0F",
};
// Desaturated to the same ink-limited tonal palette as DOMAIN_COLORS so the whole
// tag row reads as one muted print-ink system (not bright modern badges).
const SEVERITY_COLORS = {
  CRITICAL: { bg: "#6E1A1A", text: "#fff" }, HIGH: { bg: "#7A3E12", text: "#fff" },
  MODERATE: { bg: "#6B551E", text: "#fff" }, LOW: { bg: "#1A4D2E", text: "#fff" },
};
const COUNTRY_FLAGS = {
  "Ukraine": "🇺🇦", "Iran": "🇮🇷", "United States / China": "🇺🇸 / 🇨🇳",
  "South China Sea": "🌏", "Europe": "🇪🇺",
};
const domainColor = (d) => DOMAIN_COLORS[String(d || "").toUpperCase()] || "var(--color-text-primary)";
const countryFlag = (c) => COUNTRY_FLAGS[c] || "🌍";

// ISO 3166 alpha-3 → [display name, alpha-2] for the actors that appear in briefs.
const ISO3 = {
  USA: ["United States", "US"], CHN: ["China", "CN"], RUS: ["Russia", "RU"], UKR: ["Ukraine", "UA"],
  IRN: ["Iran", "IR"], ISR: ["Israel", "IL"], PRK: ["North Korea", "KP"], KOR: ["South Korea", "KR"],
  JPN: ["Japan", "JP"], TWN: ["Taiwan", "TW"], IND: ["India", "IN"], PAK: ["Pakistan", "PK"],
  PHL: ["Philippines", "PH"], VNM: ["Vietnam", "VN"], BEL: ["Belgium", "BE"], FRA: ["France", "FR"],
  DEU: ["Germany", "DE"], GBR: ["United Kingdom", "GB"], SDN: ["Sudan", "SD"], TCD: ["Chad", "TD"],
  SAU: ["Saudi Arabia", "SA"], TUR: ["Turkey", "TR"], SYR: ["Syria", "SY"], IRQ: ["Iraq", "IQ"],
  YEM: ["Yemen", "YE"], LBN: ["Lebanon", "LB"], EGY: ["Egypt", "EG"], BRA: ["Brazil", "BR"],
  ZAF: ["South Africa", "ZA"], IDN: ["Indonesia", "ID"], AUS: ["Australia", "AU"], POL: ["Poland", "PL"],
  SWE: ["Sweden", "SE"], FIN: ["Finland", "FI"], NOR: ["Norway", "NO"], NLD: ["Netherlands", "NL"],
  ITA: ["Italy", "IT"], ESP: ["Spain", "ES"], CAN: ["Canada", "CA"], MEX: ["Mexico", "MX"],
  EU: ["European Union", "EU"], BEL_EU: ["European Union", "EU"],
};
// alpha-2 → flag emoji via regional-indicator code points.
const flagEmoji = (iso2) => (iso2 && iso2.length === 2)
  ? String.fromCodePoint(...[...iso2.toUpperCase()].map((c) => 0x1F1E6 + c.charCodeAt(0) - 65))
  : "";
// Resolve a briefing item (KD with geographic_reference, or MP with country/region)
// to a clean { name, flag }. Falls back to the region string + globe when unknown.
const resolveCountry = (src) => {
  const loc = (src && src.geographic_reference && src.geographic_reference.primary_location) || {};
  const raw = String((loc.country || (src && src.country) || "")).trim();
  const region = String((src && src.region) || loc.region || loc.name || "").trim();
  if (raw) {
    const up = raw.toUpperCase();
    if (ISO3[up]) return { name: ISO3[up][0], flag: flagEmoji(ISO3[up][1]) };
    const hit = Object.values(ISO3).find(([n]) => n.toLowerCase() === raw.toLowerCase());
    if (hit) return { name: hit[0], flag: flagEmoji(hit[1]) };
    if (raw.length === 2) return { name: region || raw, flag: flagEmoji(raw) };
    return { name: raw, flag: "🌍" };
  }
  if (region) return { name: region, flag: "🌍" };
  return { name: "", flag: "" };
};

// Shared card status line: STATUS + country name + flag, small caps, one line.
// Used by both Key Developments (REPORTED) and Monitoring Priorities (FORECASTED).
const CardStatusLine = ({ status, country }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 9, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-text-light)", marginBottom: 4 }}>
    <span>{status}</span>
    {country && country.name && <span style={{ letterSpacing: "0.06em" }}>{country.name}</span>}
    {country && country.flag && <span aria-hidden="true" style={{ fontSize: 13, letterSpacing: "normal" }}>{country.flag}</span>}
  </div>
);

// Shared metadata row: Domain tile → Severity tile → Timeframe → PE (identical order
// in both sections). `dashed` selects the conceptual variant: KD = solid FILLED tiles
// (reported / already happened); MP = EMPTY OUTLINE tiles with dashed borders
// (forecast / not yet happened). Same colors either way — fill vs. outline only.
const CardMetaRow = ({ domain, severity, timeframe, pe, dashed = false }) => {
  const dc = domainColor(domain);
  const sv = SEVERITY_COLORS[String(severity || "").toUpperCase()] || { bg: "#555", text: "#fff" };
  const charcoal = "var(--color-charcoal, #0D0D0D)";
  return (
    <div style={{ display: "flex", gap: 8, fontSize: 11, flexWrap: "wrap", alignItems: "center" }}>
      {domain && (dashed
        ? <span className="bi-tile" style={{ padding: "4px 8px", border: `1px dashed ${dc}`, color: dc, background: "transparent", borderRadius: 3, textTransform: "uppercase", fontWeight: 600, fontSize: 11 }}>{String(domain)}</span>
        : <span className="bi-tile bi-tile--plate" style={{ padding: "4px 8px", background: dc, color: "#fff", borderRadius: 3, textTransform: "uppercase", fontWeight: 600, fontSize: 11 }}>{String(domain)}</span>)}
      {severity && (dashed
        ? <span className="bi-tile" style={{ padding: "4px 8px", border: `1px dashed ${sv.bg}`, color: sv.bg, background: "transparent", borderRadius: 3, textTransform: "uppercase", fontWeight: 700, fontSize: 10 }}>{String(severity)}</span>
        : <span className="bi-tile bi-tile--plate" style={{ padding: "4px 8px", background: sv.bg, color: sv.text, borderRadius: 3, textTransform: "uppercase", fontWeight: 600, fontSize: 10 }}>{String(severity)}</span>)}
      {timeframe && <span className="bi-tile" style={{ padding: "4px 8px", border: `1px ${dashed ? "dashed" : "solid"} var(--color-text-light)`, borderRadius: 3, color: "var(--color-text-light)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>◷ {String(timeframe)}</span>}
      {(pe != null && pe !== "") && (dashed
        ? <span className="bi-tile" style={{ padding: "4px 8px", border: `1px dashed ${charcoal}`, color: charcoal, background: "transparent", borderRadius: 3, fontWeight: 700, fontSize: 11 }}>PE {String(pe)}</span>
        : <span className="bi-tile bi-tile--plate" style={{ padding: "4px 8px", background: charcoal, color: "#fff", borderRadius: 3, fontWeight: 700, fontSize: 11 }}>PE {String(pe)}</span>)}
    </div>
  );
};

const parseISODate = (dateStr) => {
  if (!dateStr) return null;
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export default function PublishedBriefingView({ briefingId, showVersion = false, showEditHistory = false, onEdit = null, subscriberView = false, standalone = false }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sankeyFailed, setSankeyFailed] = useState(false);

  useEffect(() => {
    const fetchPublished = async () => {
      try {
        setLoading(true);
        console.log(`📖 PublishedBriefingView: Fetching briefing ${briefingId}`);
        // Send the auth token (when present) so the server serves the full brief
        // to Business+ subscribers; logged-out/Third Class get the preview payload.
        const tok = (typeof localStorage !== "undefined") && localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/briefings/${briefingId}/published`,
          tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        console.log(`📖 PublishedBriefingView: Received data, status=${data.status}`);
        setBriefing(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error loading published briefing:', err);
      } finally {
        setLoading(false);
      }
    };
    if (briefingId) fetchPublished();
  }, [briefingId]);

  if (loading) return <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--color-text-light)" }}>Loading briefing...</div>;
  if (error) return <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--threat-critical, #E74C3C)" }}>Error: {error}</div>;
  if (!briefing) return <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--color-text-light)" }}>Briefing not found</div>;

  const isSunday = parseISODate(briefing.date)?.getDay() === 0;
  const dateStr = (() => {
    const d = parseISODate(briefing.date);
    if (!d) return 'Unknown Date';
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  })();

  const ensureString = (val) => {
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) return val.map(item => typeof item === 'string' ? item : JSON.stringify(item)).join('\n');
    if (typeof val === 'object' && val !== null) return JSON.stringify(val, null, 2);
    return String(val || '');
  };

  const sectionStyle = {
    marginBottom: "var(--space-8, 32px)",
    paddingBottom: "var(--spacing-lg, 24px)",
    borderBottom: "2px solid var(--color-charcoal)",
  };

  // Bold small-caps section header, consistent across every section break, with
  // generous vertical rhythm from the existing spacing tokens.
  const sectionTitle = {
    fontFamily: "var(--font-heading)",
    fontSize: 22,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--color-text-primary)",
    margin: "0 0 var(--spacing-lg, 24px) 0",
    // Neutralize the global h2 rule (border-bottom + padding-bottom) so these
    // semantic headings look identical to the prior <div> titles.
    padding: 0,
    borderBottom: "none",
    lineHeight: 1.2,
  };

  const contentBlock = {
    fontSize: 13,
    lineHeight: 1.7,
    color: "var(--color-text-primary)",
    marginBottom: 16,
    padding: 12,
    background: "rgba(13,13,13,0.05)",
    borderRadius: 3,
    borderLeft: "3px solid var(--color-text-light)",
  };

  // Subscriber mode = the real customer-facing render (used by the "Today's Briefing"
  // tab). Strip editor-only section numbering and internal "(SUNDAY ONLY)" labels; the
  // podcast-scaffolding sections (Audio Intro/Transitions/Outro) and the duplicate header
  // are hidden entirely below.
  const secTitle = (label) => subscriberView
    ? label.replace(/^\d+\.\s*/, "").replace(/\s*\(SUNDAY ONLY\)/i, "")
    : label;

  // Threat severity → the shared --threat-* ramp (used as a swatch accent only;
  // text stays ink for AA contrast). Mirrors the deck accent in the tab shell.
  const THREAT_RAMP = {
    CRITICAL: "var(--threat-critical)", HIGH: "var(--threat-high)",
    ELEVATED: "var(--threat-elevated)", MODERATE: "var(--threat-moderate)", LOW: "var(--threat-low)",
  };
  const threatColor = (lvl) => THREAT_RAMP[String(lvl || "").toUpperCase().trim()] || "var(--color-charcoal)";

  // Key Takeaway is rendered as flowing editorial prose — the field names
  // (Thematic Context / Strategic Analysis / Pattern / Overall Implication) are
  // editorial scaffolding and must NOT surface as visible labels or boxes.
  const ktParagraph = {
    fontFamily: "var(--font-body)",
    fontSize: "var(--font-size-base)",
    lineHeight: "var(--line-height-relaxed)",
    color: "var(--color-text-primary)",
    margin: "0 0 16px 0",
  };
  // Literary credit — mirror the pull-quote attribution recipe (subheading family,
  // roman/uppercase, tracked) rather than the mono family reserved for machine metadata.
  const ktAttribution = {
    fontFamily: "var(--font-subheading)",
    fontSize: "0.8em",
    fontWeight: 700,
    fontStyle: "normal",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--color-text-light)",
  };

  return (
    <div className="bi-briefing" style={{
      fontFamily: "var(--font-body)",
      color: "var(--color-text-primary)",
      lineHeight: 1.7,
    }}>
      {/* Standalone subscriber masthead — used by public /view/:id share links,
          where there is no outer tab shell to supply a header. Mirrors the
          customer "Today's Briefing" masthead (title + date + podcast player). */}
      {subscriberView && standalone && (
        <header className="briefing-header" style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: "var(--font-display)", margin: 0 }}>Daily Assessment</h1>
          <p className="briefing-date">{dateStr}</p>
          {briefing.audio?.audio_url && (
            <AudioPlayer audioUrl={briefing.audio.audio_url} title="Daily Assessment" />
          )}
        </header>
      )}

      {/* Header — the tab shell supplies its own header in subscriber mode */}
      {!subscriberView && (
        <div style={{ marginBottom: 40, textAlign: "center", paddingBottom: 20, borderBottom: "1px solid var(--color-charcoal)" }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: "0.02em", margin: "0 0 8px 0" }}>📄 DAILY ASSESSMENT</h1>
          <div style={{ color: "var(--color-text-light)", fontSize: 14, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {dateStr}
          </div>
        </div>
      )}

      {/* 1. AUDIO INTRO - podcast scaffolding, editor-only */}
      {!subscriberView && (
        <div style={sectionStyle}>
          <div style={sectionTitle}>1. Audio Intro</div>
          <div style={contentBlock}>
            🎵 Auto-generated podcast opening: Welcome message with long music intro and 2-second pause
          </div>
        </div>
      )}

      {/* 2. KEY TAKEAWAY */}
      <div style={sectionStyle}>
        {/* Dateline (subscriber render only) — sits just above the lede, wire-service style. */}
        {subscriberView && (
          <div className="bi-dateline" style={{ clear: "left", fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-text-light)", marginBottom: 10 }}>
            {dateStr.toUpperCase()} &mdash;
          </div>
        )}
        <h2 style={sectionTitle}>{secTitle("2. Key Takeaway")}</h2>

        {/* Key Takeaway body — two woven editorial paragraphs. Editorial scaffolding
            labels (Thematic Context / Strategic Analysis / Pattern / Overall
            Implication) are intentionally NOT rendered; they are field names only. */}
        {(() => {
          // CANONICAL KEY TAKEAWAY — one format, four paragraphs. Each renders only
          // if it has content, so an unpopulated field never leaves a "null" or gap.
          //  ¶1 Lede (clarity):   opener → quote → tie-in → topic sentence
          //  ¶2 Posture:          threat rationale
          //  ¶3 Pattern:          observation → significance → narrative thread
          //  ¶4 Purpose:          continuity → implications
          const kt = briefing.written?.key_takeaway || {};
          const tc = kt.thematic_context || {};
          const sa = kt.strategic_analysis || {};
          const pat = sa.pattern;
          const patObj = pat && typeof pat === "object" ? pat : null;
          const observation = patObj ? ensureString(patObj.observation || "") : ensureString(pat || "");
          const significance = patObj ? ensureString(patObj.significance || "") : "";
          const narrativeThread = patObj ? ensureString(patObj.narrative_thread || "") : "";

          const opener = tc.opener ? ensureString(tc.opener) : "";
          const quote = tc.quote ? ensureString(tc.quote) : "";
          const author = tc.author ? ensureString(tc.author) : "";
          const tie = tc.tieIn ? ensureString(tc.tieIn) : "";
          const topic = tc.topic_sentence ? ensureString(tc.topic_sentence) : "";
          const rationale = kt.threat_level_rationale ? ensureString(kt.threat_level_rationale) : "";
          const implications = kt.overall_implications ? ensureString(kt.overall_implications) : "";
          const continuityRaw = kt.continuity ? ensureString(kt.continuity) : "";
          // Avoid echoing the narrative thread (used in ¶3) as continuity in ¶4.
          const continuity = continuityRaw && continuityRaw !== narrativeThread ? continuityRaw : "";
          const lvl = String(kt.threat_level || "").toUpperCase();

          const p1 = opener || quote || tie || topic;
          const p2 = rationale;
          const p3 = [observation, significance, narrativeThread].filter(Boolean).join(" ");
          const p4 = [continuity, implications].filter(Boolean).join(" ");

          // CANONICAL five-paragraph unit (editorial pass). When present + valid it is the
          // single source of the rendered Key Takeaway; otherwise fall back to the field-based
          // four-paragraph composition below. Same anchor slots in both paths:
          //   map after ¶1 · pull-quote beside ¶3 · Sankey after ¶4.
          let canonical = kt.paragraphs;
          if (typeof canonical === "string") { try { canonical = JSON.parse(canonical); } catch { canonical = null; } }
          const useCanonical = Array.isArray(canonical) && canonical.length === 5 &&
            canonical.every((p) => p && typeof p.text === "string" && p.text.trim());
          const ct = useCanonical ? canonical.map((p) => ensureString(p.text)) : [];

          // Shared anchor blocks (identical in both render paths).
          const threatStamp = kt.threat_level && (standalone || !subscriberView) ? (
            <div className="kt-threat-stamp" data-level={lvl.toLowerCase()}
              style={{ float: "right", display: "inline-flex", alignItems: "center", gap: 6,
                margin: "0 0 8px 16px", fontFamily: "var(--font-mono)", fontSize: 11,
                letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--color-text-primary)",
                border: `1px solid ${threatColor(lvl)}`, padding: "3px 8px" }}>
              <span aria-hidden="true" style={{ width: 8, height: 8, background: threatColor(lvl), display: "inline-block", flexShrink: 0 }} />
              Threat Level: {kt.threat_level}
            </div>
          ) : null;

          const mapFigure = (
            <>
              <figure className="bi-hero" style={{ margin: "20px 0", clear: "both" }}>
                <OperationalMap
                  points={(briefing.written?.key_developments || []).map((d, i) => {
                    const loc = d && d.geographic_reference && d.geographic_reference.primary_location;
                    return loc ? {
                      lat: Number(loc.latitude), lng: Number(loc.longitude),
                      kd: i + 1,
                      label: d.map_label || loc.name || loc.region || loc.country || d.region || "",
                      severity: d.severity,
                    } : null;
                  }).filter(Boolean)}
                  threatColor={threatColor}
                  fallbackSvgUrl={briefing.written?.map?.svg_url}
                />
                <figcaption className="bi-hero__credit" style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                  <span>Operational map — key developments.</span>
                  {["Critical", "High", "Moderate", "Low"].map((l) => (
                    <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <span aria-hidden="true" style={{ width: 9, height: 9, background: threatColor(l), border: "1px solid var(--color-atomic)", flexShrink: 0 }} />
                      {l}
                    </span>
                  ))}
                  <span style={{ marginLeft: "auto", fontSize: 10, opacity: 0.8 }}>Map © Mapbox © OpenStreetMap</span>
                </figcaption>
              </figure>
              <div style={{ clear: "both" }} />
            </>
          );

          const pullQuote = (() => {
            const devs = briefing.written?.key_developments || [];
            const pd = devs.find((d) => d && d.implications) || devs[0] || {};
            const raw = ensureString(pd.implications || pd.headline || "");
            const pull = (raw.split(/(?<=[.!?])\s+/)[0] || raw).trim();
            return pull ? <PulledQuote attribution={pd.headline ? ensureString(pd.headline) : undefined}>{pull}</PulledQuote> : null;
          })();

          const sankeyFigure = (briefing.id || briefing.written?.logistics_svg_url) ? (
            sankeyFailed ? (
              <VisualFallback label="Supply-chain diagram is being prepared" />
            ) : (
              <figure style={{ margin: "20px 0", border: "2px solid var(--color-atomic)", padding: 10, background: "var(--color-bg-primary)", clear: "both" }}>
                <EngravingImage
                  src={briefing.id ? `${API_URL}/api/briefings/${briefing.id}/logistics-sankey.svg` : briefing.written?.logistics_svg_url}
                  alt="Critical technologies — today's materials exposure, derived from the day's Key Developments"
                  onError={() => setSankeyFailed(true)}
                />
              </figure>
            )
          ) : null;

          if (useCanonical) {
            // Fixed five-paragraph skeleton — deterministic anchor placement.
            return (
              <>
                {threatStamp}
                {/* ¶1 Lede (drop cap) — quote is woven into the prose by the composer. */}
                <p className="kt-paragraph kt-paragraph--one bi-kt-dropcap" style={ktParagraph}>{ct[0]}</p>
                {mapFigure}
                {/* ¶2 Posture */}
                <p className="kt-paragraph kt-paragraph--two" style={ktParagraph}>{ct[1]}</p>
                {pullQuote}
                {/* ¶3 Pattern */}
                <p className="kt-paragraph kt-paragraph--three" style={ktParagraph}>{ct[2]}</p>
                {sankeyFigure}
                {/* ¶4 Implications */}
                <p className="kt-paragraph kt-paragraph--four" style={ktParagraph}>{ct[3]}</p>
                {/* ¶5 Outlook — the closer */}
                <p className="kt-paragraph kt-paragraph--five" style={ktParagraph}>{ct[4]}</p>
              </>
            );
          }

          return (
            <>
              {/* Threat level: floated margin stamp. Suppressed in the subscriber tab
                  (the masthead deck already shows it there); kept on share links /
                  editor preview, which have no deck, to avoid losing the indicator. */}
              {threatStamp}

              {/* ¶1 Lede — opener → quote + attribution → tie-in → topic sentence.
                  Drop cap only when the opener leads (so the initial is a letter, not a quote mark). */}
              {p1 && (
                <p className={`kt-paragraph kt-paragraph--one${opener ? " bi-kt-dropcap" : ""}`} style={ktParagraph}>
                  {opener && <>{opener}{" "}</>}
                  {quote && (
                    <span className="kt-quote">
                      <em style={{ fontSize: "1.05em" }}>&ldquo;{quote}&rdquo;</em>
                      {author && <span className="kt-attribution" style={ktAttribution}> &mdash; {author}</span>}
                    </span>
                  )}
                  {tie && <>{" "}{tie}</>}
                  {topic && <>{" "}{topic}</>}
                </p>
              )}
              {/* Operational map — placed between ¶1 and ¶2 of the Key Takeaway. */}
              {mapFigure}

              {/* ¶2 Posture — threat rationale (now sits below the operational map) */}
              {p2 && <p className="kt-paragraph kt-paragraph--two" style={ktParagraph}>{p2}</p>}

              {/* Pull-quote callout — ONE sentence (the hard data point), floated beside ¶3. */}
              {pullQuote}
              {/* ¶3 Pattern — observation → significance → narrative thread */}
              {p3 && <p className="kt-paragraph kt-paragraph--three" style={ktParagraph}>{p3}</p>}

              {/* Critical Technologies Sankey — sits BETWEEN ¶3 and ¶4. Full-brief only,
                  guarded by ¶3 so it never appears in the metered preview. */}
              {p3 && sankeyFigure}
              {/* ¶4 Purpose — continuity → implications */}
              {p4 && <p className="kt-paragraph kt-paragraph--four" style={ktParagraph}>{p4}</p>}
            </>
          );
        })()}
      </div>

      {/* Metered-preview cutoff — Business Class and above never see this (their
          payload is the full brief); Third Class stops here (gated content is
          absent from the payload, not merely hidden). */}
      {briefing.preview && (
        <PaywallBreak
          onSubscribe={() => window.dispatchEvent(new CustomEvent("bi:subscribe"))}
          onLogin={() => window.dispatchEvent(new CustomEvent("bi:login"))}
        />
      )}
      {!briefing.preview && (<>

      {/* 3. AUDIO TRANSITION 1 - podcast scaffolding, editor-only */}
      {!subscriberView && (
        <div style={sectionStyle}>
          <div style={sectionTitle}>3. Audio Transition 1</div>
          <div style={contentBlock}>
            🎵 Auto-generated: "That was the key takeaway for today, next we will explore the primary drivers behind today's assertions." (2 sec pause, short music, welcome to in-depth analysis)
          </div>
        </div>
      )}

      {/* (Critical Technologies Sankey now renders inside the Key Takeaway, between ¶3 and
          ¶4 — see above. It was relocated here from a standalone section to remove the
          duplicated title and improve flow.) */}

      {/* 5. KEY DEVELOPMENTS */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>{secTitle("5. Key Developments")}</h2>
        {briefing.written?.key_developments && briefing.written.key_developments.length > 0 ? (
          briefing.written.key_developments.map((dev, idx) => (
            <details key={idx} className="bi-kd" style={{ marginBottom: 16, background: "var(--color-bg-secondary)", borderRadius: 4, borderLeft: `4px solid ${domainColor(dev.domain)}` }}>
              <summary style={{ listStyle: "none", cursor: "pointer", padding: 16 }}>
                <CardStatusLine status="Reported" country={resolveCountry(dev)} />
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: "var(--color-text-primary)" }}>
                  KD{idx + 1}: {dev.headline || `Development ${idx + 1}`}
                </div>
                <CardMetaRow
                  domain={dev.domain}
                  severity={dev.severity}
                  timeframe={dev.timeframe ? ensureString(dev.timeframe) : null}
                  pe={dev.probabilistic_assessment?.forward_pe}
                />
              </summary>
              <div style={{ padding: "0 16px 16px" }}>
              {dev.assessment && <div style={contentBlock}>{ensureString(dev.assessment)}</div>}
              {dev.implications && <div style={contentBlock}><strong>Implications:</strong> {ensureString(dev.implications)}</div>}
              {(() => {
                const loc = dev.geographic_reference?.primary_location;
                const name = loc ? (loc.name || loc.region || [loc.city, loc.country].filter(Boolean).join(", "))
                  : (typeof dev.geographic_reference === "string" ? dev.geographic_reference : null);
                if (!name) return null;
                const lat = loc && Number(loc.latitude);
                const lng = loc && Number(loc.longitude);
                const coords = Number.isFinite(lat) && Number.isFinite(lng) ? ` (${lat.toFixed(2)}, ${lng.toFixed(2)})` : "";
                return <div className="bi-coord" style={contentBlock}><strong>Location:</strong> {ensureString(name)}{coords && <span className="bi-coord-val">{coords}</span>}</div>;
              })()}

              {/* Gamification: interactive probability-estimate widget (Business+ only —
                  this section is absent from the Third Class preview payload). */}
              {dev.probabilistic_assessment?.hypothesis && (
                <PEWidget
                  hypothesisId={`${briefing.briefing_id || briefing.id}-kd-${idx}`}
                  hypothesis={dev.probabilistic_assessment.hypothesis}
                  timeframeDays={dev.probabilistic_assessment.timeframe_days}
                  timeframeBasis={dev.probabilistic_assessment.timeframe_basis}
                  forwardPE={dev.probabilistic_assessment.forward_pe}
                  peNarrative={dev.probabilistic_assessment.pe_narrative}
                  baseRate={dev.probabilistic_assessment.base_rate}
                  baseRateBasis={dev.probabilistic_assessment.base_rate_basis}
                  sourceReliability={dev.source_assessment?.reliability}
                  sourceCredibility={dev.source_assessment?.credibility}
                  sourceBasis={dev.source_assessment?.basis}
                  briefingDate={briefing.date}
                />
              )}

              {Array.isArray(dev.sources) && dev.sources.filter((s) => s && s.url).length > 0 && (
                <div style={{ ...contentBlock, fontSize: 12 }}>
                  <strong>Additional resources:</strong>
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {dev.sources.filter((s) => s && s.url).slice(0, 5).map((s, si) => {
                      const raw = String(s.url);
                      const clean = raw.replace(/^Suggested:\s*/i, "");
                      const suggested = /^Suggested:/i.test(raw);
                      return (
                        <li key={si} style={{ marginBottom: 3 }}>
                          <a href={clean} target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-steel, #1A1A1A)" }}>
                            {ensureString(s.title || clean)}
                          </a>
                          {suggested && <span style={{ color: "var(--color-text-light)", fontStyle: "italic" }}> (suggested)</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              </div>
            </details>
          ))
        ) : (
          <div style={{ color: "var(--color-text-light)" }}>No key developments recorded</div>
        )}
      </div>

      {/* 7. AUDIO TRANSITION 2 - podcast scaffolding, editor-only */}
      {!subscriberView && isSunday && (
        <div style={sectionStyle}>
          <div style={sectionTitle}>7. Audio Transition 2 (SUNDAY ONLY)</div>
          <div style={contentBlock}>
            🎵 Auto-generated: Transition to Weekly Review with reflection narration and short music
          </div>
        </div>
      )}

      {/* 8. WEEKLY REVIEW - SUNDAY ONLY */}
      {isSunday && briefing.written?.weekly_review?.modules && (
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>{secTitle("8. Weekly Review (SUNDAY ONLY)")}</h2>

          {briefing.written.weekly_review.modules.bluf && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-light)", marginBottom: 8 }}>TL;DR</div>
              <div style={contentBlock}>{ensureString(briefing.written.weekly_review.modules.bluf)}</div>
            </div>
          )}

          {/* Cross-Impact Assessment — customer-facing domain-severity heatmap (method concealed) */}
          {briefing.written.weekly_review.modules.dime_pmesii && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-light)", marginBottom: 8 }}>Cross-Impact Assessment</div>
              <CrossImpactAssessment matrix={briefing.written.weekly_review.modules.dime_pmesii} />
            </div>
          )}

          {[
            ["Domain Cascade Detection", briefing.written.weekly_review.modules.domain_cascade],
            ["Supply-Chain Vulnerability & Choke Points", briefing.written.weekly_review.modules.supply_chain],
            ["Counter-Technology Requirements", briefing.written.weekly_review.modules.counter_tech],
            ["Course-of-Action Forecast", briefing.written.weekly_review.modules.course_of_action],
          ].map(([label, val]) => (val ? (
            <div key={label} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-light)", marginBottom: 8 }}>
                {label}
              </div>
              <div style={contentBlock}>{ensureString(val)}</div>
            </div>
          ) : null))}

          {/* Integrity Annex (written-only) */}
          {briefing.written.weekly_review.modules.integrity_annex && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-light)", marginBottom: 8 }}>
                Integrity Annex
              </div>
              <div style={contentBlock}>{ensureString(briefing.written.weekly_review.modules.integrity_annex)}</div>
              {!subscriberView && (
                <div style={{ fontSize: 11, color: "var(--color-text-light)", fontStyle: "italic", marginTop: 12 }}>
                  🎵 Audio conclusion: "Please visit bearing-intelligence.com for this week's publication integrity annex, where you will find detailed explanations..."
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 9. AUDIO TRANSITION 3 - podcast scaffolding, editor-only */}
      {!subscriberView && (
        <div style={sectionStyle}>
          <div style={sectionTitle}>9. Audio Transition 3</div>
          <div style={contentBlock}>
            🎵 Auto-generated: (2 sec pause, short music) "The balance of the probabilities suggests that over the next three days we will closely watch the follow to impact our world." (2 sec pause)
          </div>
        </div>
      )}

      {/* 10. MONITORING PRIORITIES */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>{secTitle("10. Monitoring Priorities")}</h2>
        {briefing.written?.monitoring_priorities && Array.isArray(briefing.written.monitoring_priorities) && briefing.written.monitoring_priorities.length > 0 ? (
          briefing.written.monitoring_priorities.slice(0, 3).map((item, idx) => {
            const obj = (item && typeof item === "object") ? item : null;
            const text = obj ? (obj.headline || obj.hypothesis || obj.text || obj.meta_hypothesis || "") : ensureString(item);
            const pe = obj ? (obj.pe ?? obj.forward_pe) : null;
            const domain = obj ? obj.domain : null;
            const tf = obj ? (obj.timeframe || (obj.timeframe_days ? `${obj.timeframe_days} days` : null)) : null;
            const severity = obj ? obj.severity : null;
            const region = obj ? (obj.region || obj.country) : null;
            // ¶1 current friction + potential action; ¶2 strategic implications (legacy: single impact).
            const friction = obj ? (obj.friction_action || obj.impact) : null;
            const implications = obj ? obj.implications : null;
            return (
              <details key={idx} className="bi-mp" style={{ marginBottom: 16, background: "var(--color-bg-secondary)", borderRadius: 4, borderLeft: `4px dashed ${domainColor(domain)}` }}>
                <summary style={{ listStyle: "none", cursor: "pointer", padding: 16 }}>
                  <CardStatusLine status="Forecasted" country={resolveCountry(obj || {})} />
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: "var(--color-text-primary)" }}>
                    MP{idx + 1}: {ensureString(text)}
                  </div>
                  <CardMetaRow domain={domain} severity={severity} timeframe={tf ? ensureString(tf) : null} pe={pe} dashed />
                </summary>
                <div style={{ padding: "0 16px 16px" }}>
                  {friction && <div style={{ fontSize: 13, lineHeight: 1.7, color: "var(--color-text-primary)", marginBottom: 8 }}>{ensureString(friction)}</div>}
                  {implications && <div style={{ fontSize: 13, lineHeight: 1.7, color: "var(--color-text-primary)" }}><strong>Strategic implications:</strong> {ensureString(implications)}</div>}
                </div>
              </details>
            );
          })
        ) : (
          <div style={{ color: "var(--color-text-light)" }}>No monitoring priorities recorded</div>
        )}
      </div>

      {/* 11. AUDIO OUTRO - podcast scaffolding, editor-only */}
      {!subscriberView && (
        <div style={{ marginBottom: 0 }}>
          <div style={sectionTitle}>11. Audio Outro</div>
          <div style={contentBlock}>
            🎵 Auto-generated: "Thank you for listening." Sales pitch, tagline, long music outro
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
